import { useEffect, useState } from "react";
import { api, formatMoney } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Landmark } from "lucide-react";

const COLORS = ["#1E2A9C", "#29B7E3", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444"];

export default function BankAccounts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", bank_name: "", iban: "", currency: "TRY", balance: "", color: COLORS[0] });

  const load = async () => setItems((await api.get("/bank-accounts")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/bank-accounts", { ...form, balance: Number(form.balance || 0) });
      toast.success("Banka hesabı eklendi");
      setOpen(false); setForm({ name: "", bank_name: "", iban: "", currency: "TRY", balance: "", color: COLORS[0] });
      load();
    } catch { toast.error("Ekleme başarısız"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/bank-accounts/${id}`);
    toast.success("Silindi"); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Hesap Yönetimi</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">Banka Hesapları</h1>
        </div>
        <button data-testid="add-bank" onClick={() => setOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] transition shadow-lg shadow-[#1E2A9C]/20"><Plus className="w-4 h-4" /> Yeni Hesap</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((b) => (
          <div key={b.id} className="relative rounded-2xl p-6 text-white overflow-hidden group" style={{ background: `linear-gradient(135deg, ${b.color}, ${b.color}CC)` }}>
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
            <div className="absolute -right-16 -bottom-16 w-40 h-40 rounded-full bg-white/5" />
            <button data-testid={`del-bank-${b.id}`} onClick={() => del(b.id)} className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition text-white/70 hover:text-white"><Trash2 className="w-4 h-4" /></button>
            <Landmark className="w-8 h-8 opacity-80" />
            <div className="mt-8">
              <div className="text-xs uppercase tracking-widest opacity-70 font-bold">{b.bank_name}</div>
              <div className="text-xl font-display font-bold">{b.name}</div>
            </div>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">Bakiye · {b.currency}</div>
              <div className="font-mono-num text-3xl font-extrabold">{formatMoney(b.balance, b.currency)}</div>
            </div>
            <div className="mt-4 font-mono-num text-[11px] opacity-80">{b.iban || "IBAN eklenmedi"}</div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-slate-400 text-center py-16">Henüz banka hesabı eklenmedi</div>}
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4">
          <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-lg p-8 relative">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="font-display text-2xl font-extrabold mb-6">Yeni Banka Hesabı</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Banka Adı</label>
                <input required data-testid="bank-name" value={form.bank_name} onChange={(e) => setForm({...form, bank_name: e.target.value})} placeholder="Garanti, İş Bankası..." className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Hesap Adı</label>
                <input required data-testid="bank-title" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ana Hesap, Vadeli..." className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Para Birimi</label>
                <select data-testid="bank-currency" value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  {["TRY","USD","EUR"].map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Başlangıç Bakiye</label>
                <input type="number" step="0.01" data-testid="bank-balance" value={form.balance} onChange={(e) => setForm({...form, balance: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">IBAN</label>
                <input data-testid="bank-iban" value={form.iban} onChange={(e) => setForm({...form, iban: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Renk</label>
                <div className="flex gap-2 mt-2">{COLORS.map((c) => <button type="button" key={c} onClick={() => setForm({...form, color: c})} className={`w-9 h-9 rounded-full ${form.color === c ? "ring-2 ring-offset-2 ring-slate-800" : ""}`} style={{ background: c }} />)}</div></div>
            </div>
            <button data-testid="bank-submit" className="w-full mt-6 py-3 bg-[#1E2A9C] text-white rounded-lg font-bold">Kaydet</button>
          </form>
        </div>
      )}
    </div>
  );
}
